import React, { useEffect, useMemo, useState } from 'react';
import OutreachShareMenu from '../Common/OutreachShareMenu';
import { buildLicenseShareText } from '../../utils/outreachShare';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { ScrollText, MapPin, ClipboardList, FileStack, Landmark, Pencil, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import FormModalLayout from '../Common/FormModalLayout';
import FormSection from '../Common/FormSection';
import InteractiveMap from '../Map/InteractiveMap';
import MapLocationPicker from '../Map/MapLocationPicker';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { useApiQuery } from '../../hooks/useApi';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';
import CondicionantesEditor, {
  condicionanteFromApi,
  syncCondicionantes,
  COND_SITUACAO_OPTIONS,
} from '../Licenses/CondicionantesEditor';
import FileViewerModal from '../Licenses/FileViewerModal';
import {
  licenseService,
  processService,
  identityService,
  institutionService,
  professionalService,
} from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { ensureRichTextValue, isEmptyRichText, RichTextEditor, RichTextHtml, stripHtml } from '../RichTextEditor';
import { userFacingError } from '../../i18n/userFacingError';
import { usePermissions } from '../../hooks/usePermissions';

import { LICENCA_STATUS_LABELS as STATUS_LABELS, licencaStatusChoices } from '../../utils/licenseStatus';

const COND_SITUACAO_LABELS = Object.fromEntries(
  COND_SITUACAO_OPTIONS.map((o) => [o.value, o.label])
);

async function fetchLicenseDetailBundle(licencaId, { canInstitution = false } = {}) {
  const licRes = await licenseService.getLicenca(licencaId);
  const licenca = licRes.data;
  let condicionantes = [];
  try {
    const condRes = await licenseService.getCondicionantesByLicenca(licencaId);
    const raw = condRes.data;
    condicionantes = Array.isArray(raw) ? raw : raw?.data ?? [];
  } catch {
    condicionantes = [];
  }

  const extras = {
    tipoLicenca: null,
    processo: null,
    cliente: null,
    empreendimento: null,
    orgao: null,
  };

  const safe = async (fn) => {
    try {
      return await fn();
    } catch {
      return null;
    }
  };

  if (licenca.tipo_licenca_id) {
    const r = await safe(() => licenseService.getTipoLicenca(licenca.tipo_licenca_id));
    if (r?.data) extras.tipoLicenca = r.data;
  }
  if (licenca.processo_id) {
    const r = await safe(() => processService.getProcesso(licenca.processo_id));
    if (r?.data) extras.processo = r.data;
  }
  if (licenca.requerente_id) {
    const r = await safe(() => identityService.getClient(licenca.requerente_id));
    if (r?.data) extras.cliente = r.data;
  }
  if (licenca.empreendimento_id) {
    let ent = null;
    const direct = await safe(() => identityService.getEnterprise(licenca.empreendimento_id));
    if (direct?.data) ent = direct.data;
    if (!ent) {
      const all = await safe(() => fetchAllPages((params) => identityService.getEnterprises(params)));
      const list = Array.isArray(all) ? all : [];
      ent = list.find((e) => String(e.id) === String(licenca.empreendimento_id)) ?? null;
    }
    extras.empreendimento = ent;
  }
  if (canInstitution && licenca.orgao_licenciador_id) {
    const r = await safe(() => institutionService.getOrgao(licenca.orgao_licenciador_id));
    if (r?.data) extras.orgao = r.data;
  }

  return { licenca, condicionantes, ...extras };
}

function formatDate(v) {
  if (v == null || v === '') return '—';
  const s = String(v);
  if (s.length >= 10) return s.slice(0, 10);
  try {
    return new Date(s).toLocaleDateString('pt-BR');
  } catch {
    return s;
  }
}

const DetailRow = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 sm:text-right sm:max-w-[65%]">{children}</dd>
  </div>
);

const LicenseDetailModal = ({ licencaId, mode = 'view', onClose, onNavigateFullEdit }) => {
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canProfessional = hasPermission(P.PROFESSIONAL_ACCESS);
  const canInstitution = hasPermission(P.INSTITUTION_ACCESS);
  const { data, isLoading, error, refetch } = useApiQuery(
    ['license-detail-bundle', licencaId, canInstitution],
    () => fetchLicenseDetailBundle(licencaId, { canInstitution }),
    { enabled: !!licencaId }
  );

  const {
    data: anexosPdfRaw,
    isLoading: anexosLoading,
    refetch: refetchAnexos,
  } = useApiQuery(
    ['licenca-anexos-pdf', licencaId],
    async () => {
      const r = await licenseService.listLicencaAnexosPdf(licencaId);
      const d = r.data;
      return Array.isArray(d) ? d : d?.data ?? [];
    },
    { enabled: !!licencaId }
  );
  const anexosPdf = Array.isArray(anexosPdfRaw) ? anexosPdfRaw : [];

  const lic = data?.licenca;
  const [quickForm, setQuickForm] = useState(null);
  const [coordDraft, setCoordDraft] = useState({ latitude: null, longitude: null });
  const [condicionantesDraft, setCondicionantesDraft] = useState([]);
  const [savingCondicionantes, setSavingCondicionantes] = useState(false);
  const [viewerAnexo, setViewerAnexo] = useState(null);
  const [rtToAdd, setRtToAdd] = useState('');

  const { data: rtPersons = [] } = useQuery(
    ['license-rt-persons'],
    () => fetchAllPages((params) => identityService.getPersons(params)),
    { enabled: !!licencaId && canProfessional, staleTime: 5 * 60 * 1000 }
  );
  const { data: technicalResponsibles = [] } = useQuery(
    ['license-technical-responsibles'],
    () => fetchAllPages((params) => professionalService.getTechnicalResponsibles(params)),
    { enabled: !!licencaId && canProfessional, staleTime: 5 * 60 * 1000 }
  );
  const { data: rtAssignments = [] } = useQuery(
    ['license-rt-assignments', licencaId, data?.licenca?.empreendimento_id],
    async () => {
      const params = { license_id: licencaId };
      const r = await professionalService.getTechnicalResponsibleAssignments(params);
      const list = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
      if (data?.licenca?.empreendimento_id) {
        const er = await professionalService.getTechnicalResponsibleAssignments({
          enterprise_id: data.licenca.empreendimento_id,
        });
        const extra = Array.isArray(er.data) ? er.data : er.data?.data ?? [];
        const seen = new Set(list.map((a) => a.id));
        for (const row of extra) {
          if (!seen.has(row.id)) list.push(row);
        }
      }
      return list;
    },
    { enabled: !!licencaId && canProfessional }
  );

  const addRtMutation = useMutation(
    (technical_responsible_id) =>
      professionalService.createTechnicalResponsibleAssignment({
        technical_responsible_id,
        license_id: licencaId,
        enterprise_id: data?.licenca?.empreendimento_id || undefined,
      }),
    {
      onSuccess: () => {
        toast.success('Responsável técnico vinculado.');
        setRtToAdd('');
        queryClient.invalidateQueries(['license-rt-assignments']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );
  const removeRtMutation = useMutation(
    (assignmentId) => professionalService.deleteTechnicalResponsibleAssignment(assignmentId),
    {
      onSuccess: () => {
        toast.success('Vínculo removido.');
        queryClient.invalidateQueries(['license-rt-assignments']);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  useEffect(() => {
    if (!data?.condicionantes) {
      setCondicionantesDraft([]);
      return;
    }
    setCondicionantesDraft(data.condicionantes.map(condicionanteFromApi));
  }, [data?.condicionantes]);

  useEffect(() => {
    if (!lic) {
      setQuickForm(null);
      return;
    }
    setQuickForm({
      numero: lic.numero || '',
      status: lic.status != null ? String(lic.status) : '1',
      validade: lic.validade ? String(lic.validade).slice(0, 10) : '',
      data_prorrogacao: lic.data_prorrogacao ? String(lic.data_prorrogacao).slice(0, 10) : '',
      observacao: ensureRichTextValue(lic.observacao || ''),
      valor: lic.valor != null ? String(lic.valor) : '',
      processo_licenciamento: !!lic.processo_licenciamento,
      notificado: !!lic.notificado,
    });
  }, [lic]);

  useEffect(() => {
    const e = data?.empreendimento;
    if (!e) {
      setCoordDraft({ latitude: null, longitude: null });
      return;
    }
    setCoordDraft({
      latitude: e.latitude != null && e.latitude !== '' ? Number(e.latitude) : null,
      longitude: e.longitude != null && e.longitude !== '' ? Number(e.longitude) : null,
    });
  }, [data?.empreendimento]);

  const renewalMutation = useMutation(
    () => licenseService.transitionLicenca(licencaId, '4'),
    {
      onSuccess: () => {
        toast.success('Renovação solicitada');
        queryClient.invalidateQueries(['licencas']);
        queryClient.invalidateQueries(['license-detail-bundle', licencaId]);
        refetch();
      },
      onError: (e) => toast.error(userFacingError(e, 'updateError')),
    }
  );

  const updateMutation = useMutation(
    async (payload) => {
      const currentStatus = String(data?.licenca?.status ?? '');
      const nextStatus = payload.status != null ? String(payload.status) : currentStatus;
      const { status, ...rest } = payload;
      if (nextStatus && nextStatus !== currentStatus) {
        await licenseService.transitionLicenca(licencaId, nextStatus);
      }
      if (Object.keys(rest).length) {
        return licenseService.licencaCrud.update(licencaId, rest);
      }
      return null;
    },
    {
      onSuccess: () => {
        toast.success('Licença atualizada');
        queryClient.invalidateQueries('licencas-all-pages');
        queryClient.invalidateQueries({ queryKey: ['licencas'] });
        queryClient.invalidateQueries(['license-detail-bundle', licencaId]);
        refetch();
      },
      onError: (e) => toast.error(userFacingError(e, 'updateError')),
    }
  );

  const saveCoordsMutation = useMutation(
    ({ id, latitude, longitude }) => identityService.updateEnterprise(id, { latitude, longitude }),
    {
      onSuccess: () => {
        toast.success('Coordenadas do empreendimento salvas');
        queryClient.invalidateQueries(['license-detail-bundle', licencaId]);
        queryClient.invalidateQueries(['identity-enterprises']);
        refetch();
      },
      onError: (e) =>
        toast.error(userFacingError(e, 'licenses:coordinatesError')),
    }
  );

  const uploadAnexoPdfMutation = useMutation(
    (file) => licenseService.uploadLicencaAnexoPdf(licencaId, file),
    {
      onSuccess: () => {
        toast.success('PDF anexado');
        queryClient.invalidateQueries(['licenca-anexos-pdf', licencaId]);
        refetchAnexos();
      },
      onError: (e) =>
        toast.error(userFacingError(e, 'uploadError')),
    }
  );

  const deleteAnexoPdfMutation = useMutation(
    (anexoId) => licenseService.deleteLicencaAnexoPdf(anexoId),
    {
      onSuccess: () => {
        toast.success('Anexo removido');
        queryClient.invalidateQueries(['licenca-anexos-pdf', licencaId]);
        refetchAnexos();
      },
      onError: (e) =>
        toast.error(userFacingError(e, 'deleteError')),
    }
  );

  const handleAnexoPdfInput = (ev) => {
    const f = ev.target.files?.[0];
    ev.target.value = '';
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Envie apenas arquivos .pdf');
      return;
    }
    uploadAnexoPdfMutation.mutate(f);
  };

  const handleDownloadMergedPdf = async () => {
    if (!anexosPdf.length) {
      toast.error('Inclua ao menos um PDF antes de gerar o documento único');
      return;
    }
    try {
      const r = await licenseService.getLicencaAnexosPdfMergedBlob(licencaId);
      const blob = r.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeNum = lic?.numero ? String(lic.numero).replace(/\s+/g, '_') : licencaId.slice(0, 8);
      a.download = `licenca-${safeNum}-anexos.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF único gerado');
    } catch (e) {
      toast.error(userFacingError(e, 'licenses:pdfMergeError'));
    }
  };

  const mapEnterprises = useMemo(() => {
    const e = data?.empreendimento;
    if (!e || e.latitude == null || e.longitude == null) return [];
    return [
      {
        id: e.id,
        name: e.name || e.nome || 'Empreendimento',
        type: e.type || 'default',
        is_active: e.is_active !== false,
        description: e.description,
        address: e.address,
        city: e.city,
        state: e.state,
        latitude: Number(e.latitude),
        longitude: Number(e.longitude),
      },
    ];
  }, [data?.empreendimento]);

  const mapCenter = useMemo(() => {
    const e = data?.empreendimento;
    if (e?.longitude != null && e?.latitude != null) {
      return [Number(e.longitude), Number(e.latitude)];
    }
    return [-47.93, -15.78];
  }, [data?.empreendimento]);

  const handleSaveEnterpriseCoords = () => {
    const e = data?.empreendimento;
    if (!e?.id) {
      toast.error('Empreendimento não disponível para atualização');
      return;
    }
    if (coordDraft.latitude == null || coordDraft.longitude == null) {
      toast.error('Defina latitude e longitude no mapa ou nos campos');
      return;
    }
    if (Number.isNaN(coordDraft.latitude) || Number.isNaN(coordDraft.longitude)) {
      toast.error('Coordenadas inválidas');
      return;
    }
    saveCoordsMutation.mutate({
      id: e.id,
      latitude: coordDraft.latitude,
      longitude: coordDraft.longitude,
    });
  };

  const handleQuickSave = (e) => {
    e.preventDefault();
    if (!quickForm) return;
    let valor = null;
    if (quickForm.valor !== '') {
      const n = Number(quickForm.valor);
      if (Number.isNaN(n)) {
        toast.error('Valor inválido');
        return;
      }
      valor = n;
    }
    const payload = {
      numero: quickForm.numero.trim() || null,
      status: quickForm.status,
      validade: quickForm.validade || null,
      data_prorrogacao: quickForm.data_prorrogacao || null,
      observacao: isEmptyRichText(quickForm.observacao) ? null : (quickForm.observacao || '').trim(),
      valor,
      processo_licenciamento: quickForm.processo_licenciamento,
      notificado: quickForm.notificado,
    };
    updateMutation.mutate(payload);
  };

  const handleSaveCondicionantes = async () => {
    setSavingCondicionantes(true);
    try {
      await syncCondicionantes(licencaId, condicionantesDraft);
      toast.success('Condicionantes salvas');
      queryClient.invalidateQueries(['license-detail-bundle', licencaId]);
      refetch();
    } catch (e) {
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setSavingCondicionantes(false);
    }
  };

  const handleViewCondicionanteAnexos = async (condicionanteId) => {
    try {
      const r = await licenseService.listCondicionanteAnexos(condicionanteId);
      const d = r.data;
      const list = Array.isArray(d) ? d : d?.data ?? [];
      if (!list.length) {
        toast.error('Nenhum anexo nesta condicionante');
        return;
      }
      setViewerAnexo(list[0]);
    } catch (e) {
      toast.error(userFacingError(e, 'licenses:attachmentsError'));
    }
  };

  const showEdit = mode === 'edit';

  const shareText = useMemo(() => {
    if (!data?.licenca) return '';
    return buildLicenseShareText({
      licenca: data.licenca,
      tipoNome: data.tipoLicenca?.nome || data.tipoLicenca?.name,
      clienteNome: data.cliente?.name,
      empreendimentoNome: data.empreendimento?.name || data.empreendimento?.nome,
      condicionantes: data.condicionantes,
    });
  }, [data]);

  const shareSubject = useMemo(() => {
    if (lic?.numero) return `Licença ${lic.numero}`;
    return `Licença ${licencaId?.slice(0, 8) ?? ''}…`;
  }, [lic?.numero, licencaId]);

  return (
    <FormModalLayout
      title="Detalhes da licença"
      subtitle={
        lic?.numero
          ? `Licença ${lic.numero} · identificador ${licencaId?.slice(0, 8)}…`
          : `Identificador ${licencaId}`
      }
      headerIcon={ScrollText}
      historyEntityType="licenses"
      historyEntityId={licencaId || lic?.id}
      headerExtra={shareText ? <OutreachShareMenu subject={shareSubject} text={shareText} /> : null}
      onClose={onClose}
      size="6xl"
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Fechar
          </button>
          {String(lic?.status) === '3' ? (
            <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS]}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={renewalMutation.isLoading}
                onClick={() => renewalMutation.mutate()}
              >
                {renewalMutation.isLoading ? 'Solicitando…' : 'Solicitar renovação'}
              </button>
            </Can>
          ) : null}
          {onNavigateFullEdit ? (
            <button
              type="button"
              className="btn btn-outline inline-flex items-center gap-2 border-primary-200 text-primary-800"
              onClick={() => onNavigateFullEdit(licencaId)}
            >
              <Pencil className="h-4 w-4" />
              Edição completa (cadastro)
            </button>
          ) : null}
        </>
      }
    >
      {isLoading && (
        <p className="text-sm text-gray-600" role="status">
          Carregando detalhes…
        </p>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {userFacingError(error, 'loadError')}
        </div>
      )}
      {data && lic && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <FormSection
              id="lic-det-ident"
              title="Identificação e status"
              description="Dados principais do registro no sistema."
              icon={ScrollText}
              variant="primary"
            >
              <dl>
                <DetailRow label="Número">{lic.numero || '—'}</DetailRow>
                <DetailRow label="Status">
                  {STATUS_LABELS[String(lic.status)] || lic.status || '—'}
                </DetailRow>
                <DetailRow label="Data abertura (processo)">
                  {formatDate(lic.data_abertura_processo)}
                </DetailRow>
                <DetailRow label="Validade">{formatDate(lic.validade)}</DetailRow>
                <DetailRow label="Prorrogação">{formatDate(lic.data_prorrogacao)}</DetailRow>
                <DetailRow label="Valor (R$)">
                  {lic.valor != null && lic.valor !== '' ? Number(lic.valor).toLocaleString('pt-BR') : '—'}
                </DetailRow>
                <DetailRow label="Processo em licenciamento">
                  {lic.processo_licenciamento ? 'Sim' : 'Não'}
                </DetailRow>
                <DetailRow label="Notificado">{lic.notificado ? 'Sim' : 'Não'}</DetailRow>
                <DetailRow label="Gestão">{lic.gestao || '—'}</DetailRow>
                <DetailRow label="ID interno">
                  <span className="font-mono text-xs">{lic.id}</span>
                </DetailRow>
                <DetailRow label="Submódulos">
                  <Link to={`/license/submodules?licenca=${licencaId}`} className="text-primary-700">
                    Eventos, solicitações, certidões e documentos
                  </Link>
                </DetailRow>
              </dl>
            </FormSection>

            <FormSection
              id="lic-det-vinc"
              title="Vínculos"
              description="Tipo, processo, requerente, empreendimento e órgão."
              icon={FileStack}
              variant="slate"
            >
              <dl>
                <DetailRow label="Tipo de licença">
                  {data.tipoLicenca
                    ? `${data.tipoLicenca.nome || data.tipoLicenca.name || '—'}`
                    : lic.tipo_licenca_id
                      ? `ID: ${lic.tipo_licenca_id.slice(0, 8)}…`
                      : '—'}
                </DetailRow>
                <DetailRow label="Processo">
                  {data.processo ? (
                    <span>
                      <span className="font-medium">{data.processo.numero || 'S/n'}</span>
                      <span className="ml-2 font-mono text-xs text-gray-500">
                        {data.processo.id?.slice(0, 8)}…
                      </span>
                    </span>
                  ) : (
                    <span className="font-mono text-xs">{lic.processo_id || '—'}</span>
                  )}
                </DetailRow>
                <DetailRow label="Requerente">
                  {data.cliente ? (
                    <span>
                      {data.cliente.name || '—'}
                      {data.cliente.document || data.cliente.cpf || data.cliente.cnpj
                        ? ` · ${data.cliente.document || data.cliente.cpf || data.cliente.cnpj}`
                        : ''}
                    </span>
                  ) : (
                    <span className="font-mono text-xs">{lic.requerente_id || '—'}</span>
                  )}
                </DetailRow>
                <DetailRow label="Empreendimento">
                  {data.empreendimento ? (
                    <span>{data.empreendimento.name || data.empreendimento.nome || '—'}</span>
                  ) : (
                    <span>{lic.empreendimento_id ? `${lic.empreendimento_id.slice(0, 8)}…` : '—'}</span>
                  )}
                </DetailRow>
                <DetailRow label="Órgão licenciador">
                  {data.orgao ? (
                    <span>{data.orgao.name || '—'}</span>
                  ) : (
                    <span>
                      {lic.orgao_licenciador_id ? `${lic.orgao_licenciador_id.slice(0, 8)}…` : '—'}
                    </span>
                  )}
                </DetailRow>
                <DetailRow label="Licença vinculada (ID)">
                  {lic.licenca_vinculada_id ? (
                    <span className="font-mono text-xs">{lic.licenca_vinculada_id}</span>
                  ) : (
                    '—'
                  )}
                </DetailRow>
              </dl>
            </FormSection>
          </div>

          <FormSection
            id="lic-det-rt"
            title="Responsável técnico"
            description="Profissionais habilitados vinculados a esta licença ou ao empreendimento."
            icon={FileStack}
            variant="slate"
          >
            {rtAssignments.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum responsável técnico vinculado.</p>
            ) : (
              <ul className="divide-y rounded-lg border border-gray-200">
                {rtAssignments.map((row) => {
                  const rt = technicalResponsibles.find(
                    (t) => String(t.id) === String(row.technical_responsible_id)
                  );
                  const person = rtPersons.find((p) => String(p.id) === String(rt?.person_id));
                  return (
                    <li key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span>
                        {person?.name || rt?.professional_license_number || row.technical_responsible_id}
                        {row.license_id ? (
                          <span className="ml-2 text-xs text-gray-500">licença</span>
                        ) : null}
                        {row.enterprise_id ? (
                          <span className="ml-2 text-xs text-gray-500">empreendimento</span>
                        ) : null}
                      </span>
                      {showEdit ? (
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-800"
                          disabled={removeRtMutation.isLoading}
                          onClick={() => removeRtMutation.mutate(row.id)}
                        >
                          Remover
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
            {showEdit ? (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="form-group min-w-[220px] flex-1">
                  <label className="form-label" htmlFor="lic-rt-add">
                    Vincular responsável
                  </label>
                  <select
                    id="lic-rt-add"
                    className="form-select"
                    value={rtToAdd}
                    onChange={(e) => setRtToAdd(e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {technicalResponsibles
                      .filter(
                        (t) => !rtAssignments.some((a) => String(a.technical_responsible_id) === String(t.id))
                      )
                      .map((t) => {
                        const person = rtPersons.find((p) => String(p.id) === String(t.person_id));
                        return (
                          <option key={t.id} value={t.id}>
                            {person?.name || t.professional_license_number || t.id}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!rtToAdd || addRtMutation.isLoading}
                  onClick={() => addRtMutation.mutate(rtToAdd)}
                >
                  Vincular
                </button>
              </div>
            ) : null}
          </FormSection>

          {lic.empreendimento_id ? (
            <FormSection
              id="lic-det-map"
              title="Localização do empreendimento"
              description={
                mapEnterprises.length
                  ? 'Coordenadas cadastradas no empreendimento vinculado à licença.'
                  : 'Mapa disponível mesmo sem coordenadas — em modo edição, clique no mapa para definir o ponto e salvar.'
              }
              icon={MapPin}
              variant="emerald"
            >
              {!data.empreendimento && lic.empreendimento_id ? (
                <p className="mb-3 text-sm text-gray-600">
                  Não foi possível carregar os dados do empreendimento (ID {lic.empreendimento_id.slice(0, 8)}…). O mapa
                  abaixo permanece disponível; se o registro existir em Identidade, verifique permissões ou sincronismo
                  do ID.
                </p>
              ) : null}
              {data.empreendimento ? (
                <div className="mb-3 text-sm text-gray-600">
                  <span className="font-medium text-gray-800">
                    {data.empreendimento.name || data.empreendimento.nome}
                  </span>
                  {data.empreendimento.city || data.empreendimento.state
                    ? ` · ${[data.empreendimento.city, data.empreendimento.state].filter(Boolean).join(', ')}`
                    : ''}
                  {data.empreendimento.address ? ` · ${data.empreendimento.address}` : ''}
                </div>
              ) : null}
              <InteractiveMap
                enterprises={mapEnterprises}
                center={mapCenter}
                zoom={mapEnterprises.length ? 14 : 5}
                height="320px"
                mapClassName="bg-gray-50"
              />
              {showEdit && data?.empreendimento ? (
                <div className="mt-4 space-y-3 rounded-lg border border-gray-100 bg-slate-50/80 p-4">
                  <p className="text-sm font-medium text-gray-800">Ajustar coordenadas no mapa</p>
                  <p className="text-xs text-gray-600">
                    Clique no mapa para posicionar o marcador e salve no empreendimento vinculado.
                  </p>
                  <MapLocationPicker
                    latitude={coordDraft.latitude}
                    longitude={coordDraft.longitude}
                    editable
                    height="240px"
                    onChange={({ latitude: la, longitude: lo }) =>
                      setCoordDraft({ latitude: la, longitude: lo })
                    }
                  />
                  <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS, P.IDENTITY_ACCESS]}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={saveCoordsMutation.isLoading}
                      onClick={handleSaveEnterpriseCoords}
                    >
                      {saveCoordsMutation.isLoading ? 'Salvando…' : 'Salvar coordenadas no empreendimento'}
                    </button>
                  </Can>
                </div>
              ) : null}
            </FormSection>
          ) : null}

          <FormSection
            id="lic-det-cond"
            title="Condicionantes"
            description="Obrigações vinculadas a esta licença no cadastro."
            icon={ClipboardList}
            variant="primary"
          >
            {showEdit ? (
              <div className="space-y-4">
                <CondicionantesEditor
                  value={condicionantesDraft}
                  onChange={setCondicionantesDraft}
                  licencaId={licencaId}
                />
                <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS]}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={savingCondicionantes}
                    onClick={handleSaveCondicionantes}
                  >
                    {savingCondicionantes ? 'Salvando…' : 'Salvar condicionantes'}
                  </button>
                </Can>
              </div>
            ) : data.condicionantes?.length ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nº</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Texto</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Situação</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Validade</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Informação</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Anexos</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {data.condicionantes.map((c) => (
                      <tr key={c.id}>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-700">{c.numero}</td>
                        <td className="max-w-md px-3 py-2 text-gray-900">{c.condicionante}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                          {COND_SITUACAO_LABELS[String(c.situacao)] || c.situacao}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                          {formatDate(c.validade)}
                        </td>
                        <td className="max-w-xs px-3 py-2 text-gray-600">{c.informacao || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            onClick={() => handleViewCondicionanteAnexos(c.id)}
                          >
                            Ver anexos
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhuma condicionante cadastrada para esta licença.</p>
            )}
          </FormSection>
          {viewerAnexo ? <FileViewerModal anexo={viewerAnexo} onClose={() => setViewerAnexo(null)} /> : null}

          <FormSection
            id="lic-det-anexos-pdf"
            title="Anexos PDF da licença"
            description="Documentos em PDF que compõem a licença. Gere um único arquivo com todos os anexos na ordem cadastrada."
            icon={FileText}
            variant="slate"
          >
            {anexosLoading ? (
              <p className="text-sm text-gray-600">Carregando anexos…</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white text-sm">
                {anexosPdf.map((a) => (
                  <li
                    key={a.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5"
                  >
                    <span className="min-w-0 break-words font-medium text-gray-900">
                      {a.ordinal != null ? `${a.ordinal}. ` : ''}
                      {a.filename_original || 'anexo.pdf'}
                    </span>
                    <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS]}>
                      <button
                        type="button"
                        className="btn btn-outline btn-xs text-red-700 hover:border-red-300"
                        disabled={deleteAnexoPdfMutation.isLoading}
                        onClick={() => {
                          if (window.confirm('Remover este anexo?')) deleteAnexoPdfMutation.mutate(a.id);
                        }}
                      >
                        Remover
                      </button>
                    </Can>
                  </li>
                ))}
                {anexosPdf.length === 0 ? (
                  <li className="px-3 py-4 text-gray-500">Nenhum PDF anexado.</li>
                ) : null}
              </ul>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS]}>
                <label className="btn btn-outline inline-flex cursor-pointer items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {uploadAnexoPdfMutation.isLoading ? 'Enviando…' : 'Enviar PDF'}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    disabled={uploadAnexoPdfMutation.isLoading}
                    onChange={handleAnexoPdfInput}
                  />
                </label>
              </Can>
              <button
                type="button"
                className="btn btn-primary inline-flex items-center gap-2"
                disabled={!anexosPdf.length}
                onClick={handleDownloadMergedPdf}
              >
                Gerar licença em PDF (anexos concatenados)
              </button>
            </div>
          </FormSection>

          {stripHtml(lic.observacao) ? (
            <FormSection
              id="lic-det-obs"
              title="Observações"
              description="Texto livre registrado na licença."
              icon={Landmark}
              variant="slate"
            >
              <RichTextHtml className="text-sm text-gray-800" html={lic.observacao} />
            </FormSection>
          ) : null}

          {showEdit && quickForm && (
            <FormSection
              id="lic-det-quick"
              title="Edição rápida"
              description={`Atualize campos básicos sem abrir o cadastro completo. Vínculos (tipo, processo, ${party.one}) exigem a tela Licenças.`}
              icon={Pencil}
              variant="emerald"
            >
              <form id="lic-quick-edit-form" onSubmit={handleQuickSave} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="form-group">
                    <label className="form-label" htmlFor="qd-numero">
                      Número
                    </label>
                    <input
                      id="qd-numero"
                      className="form-input"
                      value={quickForm.numero}
                      onChange={(e) => setQuickForm((f) => ({ ...f, numero: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="qd-status">
                      Status
                    </label>
                    <select
                      id="qd-status"
                      className="form-select"
                      value={quickForm.status}
                      onChange={(e) => setQuickForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      {licencaStatusChoices(lic?.status).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="qd-val">
                      Validade
                    </label>
                    <input
                      id="qd-val"
                      type="date"
                      className="form-input"
                      value={quickForm.validade}
                      onChange={(e) => setQuickForm((f) => ({ ...f, validade: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="qd-pror">
                      Data prorrogação
                    </label>
                    <input
                      id="qd-pror"
                      type="date"
                      className="form-input"
                      value={quickForm.data_prorrogacao}
                      onChange={(e) => setQuickForm((f) => ({ ...f, data_prorrogacao: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="qd-valor">
                      Valor (R$)
                    </label>
                    <input
                      id="qd-valor"
                      type="number"
                      step="0.01"
                      className="form-input"
                      value={quickForm.valor}
                      onChange={(e) => setQuickForm((f) => ({ ...f, valor: e.target.value }))}
                    />
                  </div>
                  <div className="form-group md:col-span-2">
                    <label className="form-label" htmlFor="qd-obs">
                      Observação
                    </label>
                    <RichTextEditor
                      variant="compact"
                      value={quickForm.observacao}
                      onChange={(v) => setQuickForm((f) => ({ ...f, observacao: v }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-6 md:col-span-2">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={quickForm.processo_licenciamento}
                        onChange={(e) =>
                          setQuickForm((f) => ({ ...f, processo_licenciamento: e.target.checked }))
                        }
                      />
                      Processo em licenciamento
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={quickForm.notificado}
                        onChange={(e) => setQuickForm((f) => ({ ...f, notificado: e.target.checked }))}
                      />
                      Notificado
                    </label>
                  </div>
                </div>
                <Can any={[P.LICENSES_PAGE, P.LICENSE_LICENCAS]}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={updateMutation.isLoading}
                  >
                    {updateMutation.isLoading ? 'Salvando…' : 'Salvar alterações'}
                  </button>
                </Can>
              </form>
            </FormSection>
          )}
        </div>
      )}
    </FormModalLayout>
  );
};

export default LicenseDetailModal;
